import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { CONCEPT_RENDITION_GENERATION_STALE_TIMEOUT_MS } from '@/lib/ai-agent/image/timeouts';
import {
  type AutosaveQueue,
  createAutosaveQueue,
  mergeKeyedPatch,
} from '@/lib/concept-workbench/autosave';
import type { AspectRatioToken, SetLayer } from '@/lib/concept-workbench/types';
import { ownerScopedKey, resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import {
  ConceptRenditionConflictError,
  type LoadedVariantSet,
  type RenditionPatch,
  type SetPatch,
  conceptRenditionService,
  defaultRatiosForPlatforms,
} from '@/services/data/ConceptRenditionService';
import { logger } from '@/utils/logger';

/**
 * Load + autosave for the Concept Workbench variant set.
 *
 * `concept_rendition_sets` and `concept_renditions` are BOTH account-scoped
 * (`src/lib/identity/table-scope.generated.ts`), so the resolve-then-fetch shape
 * of `useAdUnits` / `useBrandHandles` applies verbatim: the query key carries the
 * RESOLVED owner id, never the raw session id, or two members of one account keep
 * separate caches of identical data — and the member's cache would be empty.
 */

const SET_KEY = '__set__';

const GENERATING_POLL_MS = 4_000;
const RECONCILE_POLL_MS = 30_000;

export const conceptRenditionKeys = {
  all: ['concept-renditions'] as const,
  concept: (conceptId: string, ownerId: string | undefined) =>
    ownerScopedKey(['concept-renditions', conceptId], ownerId),
};

export interface UseConceptRenditionsOptions {
  /**
   * The Concept's declared delivery platforms. Drives the default ratio set on
   * first open (spec §8.4). Omit and the fallback trio is used.
   */
  platforms?: readonly (string | null | undefined)[];
  /** Test seam. Production leaves this at the spec's 800ms. */
  debounceMs?: number;
}

type QueuedPatch = { kind: 'set'; patch: SetPatch } | { kind: 'rendition'; patch: RenditionPatch };

export function useConceptRenditions(conceptId?: string, options?: UseConceptRenditionsOptions) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [conflict, setConflict] = useState<ConceptRenditionConflictError | null>(null);
  const [pendingKeys, setPendingKeys] = useState<string[]>([]);
  const [timedOutRenditionIds, setTimedOutRenditionIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const { data: ownerId } = useQuery({
    queryKey: ['account-owner-id', user?.id],
    queryFn: () => resolveOwnerId(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
  });

  const queryKey = conceptRenditionKeys.concept(conceptId ?? 'none', ownerId);

  const query = useQuery({
    queryKey,
    enabled: Boolean(conceptId && ownerId),
    queryFn: () => conceptRenditionService.load(conceptId!, ownerId!),
    // Layer state is authored in one tab at a time and every local edit already
    // updates the cache in place. A short staleTime avoids a refetch on every
    // window focus stomping the document the user is actively dragging.
    staleTime: 60 * 1000,
    // While any rendition is still `is_generating`, poll: the server-side
    // completion (`attachGeneratedMediaByCorrelation`) writes the media onto
    // the row with no browser involved, so a tab that reloaded mid-request
    // only ever SEES that write by re-reading. `refetchInterval` ignores
    // `staleTime`, so the 60s freshness window cannot hold the poll back.
    refetchInterval: (current) =>
      current.state.data?.document.variants.some((variant) => variant.isGenerating)
        ? GENERATING_POLL_MS
        : false,
  });

  /**
   * The AUTHORITATIVE versions, held outside the store the user is editing.
   *
   * The store mutates its `VariantState.version` never — it is a local document.
   * Reading an expected version off it would send a stale number into the CAS on
   * the second save of a session and turn every autosave into a conflict.
   */
  const versionsRef = useRef<{ set: number; renditions: Record<string, number> }>({
    set: 0,
    renditions: {},
  });
  const setRowIdRef = useRef<string | null>(null);
  const loadedRef = useRef<LoadedVariantSet | null>(null);

  useEffect(() => {
    const loaded = query.data;
    if (!loaded) return;
    loadedRef.current = loaded;
    setRowIdRef.current = loaded.setRowId;
    versionsRef.current = {
      set: loaded.setVersion,
      renditions: Object.fromEntries(
        loaded.document.variants.map((variant) => [variant.id, variant.version]),
      ),
    };
  }, [query.data]);

  /**
   * The cache write after a successful autosave.
   *
   * 🔴 THE BUG THIS FIXES. This used to bump ONLY the version number and leave
   * `document.layers` / a variant's `geometry` untouched — so the query cache
   * silently diverged from what was actually written the moment the FIRST edit
   * of a session saved, and stayed diverged for the rest of the session. Nothing
   * in-session noticed, because the Zustand store (not this cache) is what
   * renders. But `PersistQueryClientProvider` (`src/main.tsx`) persists this
   * cache to IndexedDB, `staleTime` (60s) then skips a background refetch on the
   * next mount, and `useConceptWorkbench`'s hydration guard only re-hydrates on
   * a VARIANT MEMBERSHIP change (by design — see its own comment) — adding or
   * editing a layer never changes membership. So a stale cache, once persisted,
   * stayed stale forever: a layer added and confirmed-saved this session would
   * silently vanish from the Layers panel and the canvas on the very next
   * reload, even though the database row was correct the whole time. Reproduced
   * and confirmed live: after adding a second text layer and reloading, the
   * IndexedDB-restored cache had 3 of the 4 layers the database actually held,
   * and no network request for `concept_rendition_sets` fired at all because the
   * (incomplete) cache still looked fresh.
   *
   * The fix: apply the SAME patch that was just confirmed written, not just its
   * version. `patch.layers` / `patch.geometry` are always FULL replacements
   * (never partial — see `diffDocuments`), so this is a plain field merge, and
   * because every merge is either "replace the whole layers array" or "replace
   * the whole geometry map for one variant", nothing here compares by value —
   * identity is whatever the patch already carried through the store's own
   * immutable mutation path.
   */
  const applyVersion = useCallback(
    (key: string, version: number, applied?: QueuedPatch) => {
      if (key === SET_KEY) versionsRef.current.set = version;
      else versionsRef.current.renditions[key] = version;

      // Precise cache maintenance, NOT an invalidate. Invalidating here would
      // refetch mid-edit and replace the document the user is dragging with the
      // server's copy of it — every 800ms.
      queryClient.setQueryData<LoadedVariantSet>(queryKey, (previous) => {
        if (!previous) return previous;
        if (key === SET_KEY) {
          const setPatch = applied?.kind === 'set' ? applied.patch : undefined;
          return {
            ...previous,
            setVersion: version,
            document: {
              ...previous.document,
              layers: setPatch?.layers ?? previous.document.layers,
              fontScaleBase: setPatch?.fontScaleBase ?? previous.document.fontScaleBase,
              mediaId:
                setPatch?.mediaId === undefined ? previous.document.mediaId : setPatch.mediaId,
            },
          };
        }
        const renditionPatch = applied?.kind === 'rendition' ? applied.patch : undefined;
        return {
          ...previous,
          document: {
            ...previous.document,
            variants: previous.document.variants.map((variant) => {
              if (variant.id !== key) return variant;
              return {
                ...variant,
                version,
                mediaId:
                  renditionPatch?.mediaId === undefined ? variant.mediaId : renditionPatch.mediaId,
                followsSetImage: renditionPatch?.followsSetImage ?? variant.followsSetImage,
                needsReview: renditionPatch?.needsReview ?? variant.needsReview,
                isGenerating: renditionPatch?.isGenerating ?? variant.isGenerating,
                position: renditionPatch?.position ?? variant.position,
                framing: renditionPatch?.framing ?? variant.framing,
                geometry: renditionPatch?.geometry ?? variant.geometry,
                overrides: renditionPatch?.overrides ?? variant.overrides,
              };
            }),
          },
        };
      });
    },
    [queryClient, queryKey],
  );

  /**
   * The queue is created ONCE, but `applyVersion` closes over `queryKey`, which
   * changes the moment `resolveOwnerId` resolves. Reading it through a latest-ref
   * is not a style preference: the first render's key ends in `undefined`, so a
   * captured closure would write every version update into a cache entry nobody
   * reads — a silent no-op leaving the visible row's version stale forever.
   */
  const applyVersionRef = useRef(applyVersion);
  applyVersionRef.current = applyVersion;

  const queueRef = useRef<AutosaveQueue<QueuedPatch> | null>(null);
  if (queueRef.current === null) {
    queueRef.current = createAutosaveQueue<QueuedPatch>({
      delayMs: options?.debounceMs,
      merge: (pending, incoming) =>
        pending.kind === 'set' && incoming.kind === 'set'
          ? { kind: 'set', patch: { ...pending.patch, ...incoming.patch } }
          : pending.kind === 'rendition' && incoming.kind === 'rendition'
            ? { kind: 'rendition', patch: mergeKeyedPatch(pending.patch, incoming.patch) }
            : incoming,
      flush: async (key, queued) => {
        if (queued.kind === 'set') {
          const setRowId = setRowIdRef.current;
          const current = loadedRef.current;
          if (!setRowId || !current) {
            throw new Error('This concept has no rendition set yet. Open it again to create one.');
          }
          const result = await conceptRenditionService.saveSet({
            setRowId,
            expectedVersion: versionsRef.current.set,
            patch: queued.patch,
            current: {
              layers: current.document.layers,
              fontScaleBase: current.document.fontScaleBase,
              mediaId: current.document.mediaId,
            },
          });
          applyVersionRef.current(SET_KEY, result.version, queued);
          return;
        }
        const result = await conceptRenditionService.saveRendition({
          renditionId: key,
          expectedVersion: versionsRef.current.renditions[key] ?? 1,
          patch: queued.patch,
        });
        applyVersionRef.current(key, result.version, queued);
      },
      onError: (_key, error) => {
        if (error instanceof ConceptRenditionConflictError) setConflict(error);
      },
      onPendingChange: setPendingKeys,
    });
  }
  const queue = queueRef.current;

  /** Drain before the tab goes away — an 800ms window is easy to lose. */
  useEffect(() => {
    const drain = () => void queue.flushAll();
    window.addEventListener('beforeunload', drain);
    return () => {
      window.removeEventListener('beforeunload', drain);
      void queue.flushAll();
    };
  }, [queue]);

  const saveSet = useCallback(
    (patch: SetPatch) => queue.enqueue(SET_KEY, { kind: 'set', patch }),
    [queue],
  );

  const saveVariant = useCallback(
    (renditionId: string, patch: RenditionPatch) =>
      queue.enqueue(renditionId, { kind: 'rendition', patch }),
    [queue],
  );

  const ensureRenditions = useMutation({
    mutationFn: (input?: { ratios?: readonly AspectRatioToken[] }) => {
      if (!conceptId) throw new Error('No concept selected.');
      if (!ownerId) throw new Error('Account access is still loading. Try again.');
      return conceptRenditionService.ensureRenditions({
        conceptId,
        ownerId,
        ratios: input?.ratios ?? defaultRatiosForPlatforms(options?.platforms ?? []),
      });
    },
    onSuccess: (loaded) => queryClient.setQueryData<LoadedVariantSet>(queryKey, loaded),
  });

  const addRatio = useMutation({
    mutationFn: (input: { aspectRatio: AspectRatioToken; layers?: readonly SetLayer[] }) => {
      if (!conceptId) throw new Error('No concept selected.');
      if (!ownerId) throw new Error('Account access is still loading. Try again.');
      const loaded = loadedRef.current;
      return conceptRenditionService.addRatio({
        conceptId,
        ownerId,
        aspectRatio: input.aspectRatio,
        position: loaded?.document.variants.length ?? 0,
        layers: input.layers ?? loaded?.document.layers ?? [],
        mediaId: loaded?.document.mediaId ?? null,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const removeRatio = useMutation({
    mutationFn: (renditionId: string) => {
      if (!ownerId) throw new Error('Account access is still loading. Try again.');
      queue.cancel(renditionId);
      return conceptRenditionService.removeRatio(renditionId, ownerId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  /**
   * `is_generating` bypasses the debounce on purpose. The Ready gate reads it,
   * and an in-memory-only flag evaporates on reload — a refreshed tab would offer
   * `Ready` on a concept whose 9:16 is still rendering. That is a false pass, and
   * §6.9 refuses those in the transition gate rather than in a help string.
   */
  const setGenerating = useMutation({
    mutationFn: async (input: {
      renditionId: string;
      isGenerating: boolean;
      generationToken?: string;
    }) => {
      const result = await conceptRenditionService.setGenerating({
        renditionId: input.renditionId,
        expectedVersion: versionsRef.current.renditions[input.renditionId] ?? 1,
        isGenerating: input.isGenerating,
        generationToken: input.generationToken,
      });
      applyVersion(input.renditionId, result.version);
      return result;
    },
    onSuccess: (result, input) => {
      queryClient.setQueryData<LoadedVariantSet>(queryKey, (previous) =>
        previous
          ? {
              ...previous,
              document: {
                ...previous.document,
                variants: previous.document.variants.map((variant) =>
                  variant.id === input.renditionId
                    ? { ...variant, isGenerating: input.isGenerating, version: result.version }
                    : variant,
                ),
              },
            }
          : previous,
      );
    },
    onError: (error) => {
      if (error instanceof ConceptRenditionConflictError) setConflict(error);
    },
  });

  const attachMedia = useMutation({
    mutationFn: async (input: { renditionId: string; mediaId: string }) => {
      if (!ownerId) throw new Error('Account access is still loading. Try again.');
      const result = await conceptRenditionService.attachMedia({
        renditionId: input.renditionId,
        ownerId,
        expectedVersion: versionsRef.current.renditions[input.renditionId] ?? 1,
        mediaId: input.mediaId,
      });
      applyVersion(input.renditionId, result.version);
      return result;
    },
    onSuccess: (result, input) => {
      queryClient.setQueryData<LoadedVariantSet>(queryKey, (previous) =>
        previous
          ? {
              ...previous,
              document: {
                ...previous.document,
                variants: previous.document.variants.map((variant) =>
                  variant.id === input.renditionId
                    ? {
                        ...variant,
                        mediaId: input.mediaId,
                        followsSetImage: false,
                        version: result.version,
                      }
                    : variant,
                ),
              },
            }
          : previous,
      );
    },
    onError: (error) => {
      if (error instanceof ConceptRenditionConflictError) setConflict(error);
    },
  });

  const reload = useCallback(async () => {
    setConflict(null);
    queue.cancel();
    await queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey, queue]);

  /**
   * THE ORPHAN RECONCILER's client-side half. `attachGeneratedMediaByCorrelation`
   * (server-side, `generate-image.post.ts`) is the durable fix for the common
   * case — the request itself completes and the row gets its media
   * regardless of the browser. This covers what THAT can't: a request that
   * never completes at all (the provider call itself crashed, timed out, or
   * the reservation/quota check failed before dispatch ever fired). Without
   * this, that row is `is_generating: true` forever, on every future load,
   * with no way out but hand-editing the database.
   */
  const reconcileStaleGenerations = useCallback(async (): Promise<string[]> => {
    if (!conceptId || !ownerId) return [];
    const reconciledIds = await conceptRenditionService.reconcileStaleGenerations({
      conceptId,
      ownerId,
      timeoutMs: CONCEPT_RENDITION_GENERATION_STALE_TIMEOUT_MS,
    });
    if (reconciledIds.length === 0) return reconciledIds;
    const reconciled = new Set(reconciledIds);
    setTimedOutRenditionIds((previous) => new Set([...previous, ...reconciledIds]));
    queryClient.setQueryData<LoadedVariantSet>(queryKey, (previous) =>
      previous
        ? {
            ...previous,
            document: {
              ...previous.document,
              variants: previous.document.variants.map((variant) =>
                reconciled.has(variant.id) ? { ...variant, isGenerating: false } : variant,
              ),
            },
          }
        : previous,
    );
    return reconciledIds;
  }, [conceptId, ownerId, queryClient, queryKey]);

  /**
   * A read stays a read: nothing is swept unless the loaded document has a
   * variant that is still generating (a reload is exactly when an orphan from
   * a PRIOR tab shows "Generating..." forever). It sweeps once as soon as that
   * is true, then on a poll while it stays true — a stale row born mid-session
   * needs the same sweep. The service SELECTs stale ids first and only writes
   * when there are any.
   */
  const hasGeneratingVariant = (query.data?.document.variants ?? []).some(
    (variant) => variant.isGenerating,
  );
  useEffect(() => {
    if (!conceptId || !ownerId) return;
    const sweep = () =>
      reconcileStaleGenerations().catch((cause: unknown) =>
        logger.error('Stale rendition generation sweep failed', cause),
      );
    if (!hasGeneratingVariant) return;
    void sweep();
    const interval = setInterval(() => void sweep(), RECONCILE_POLL_MS);
    return () => clearInterval(interval);
  }, [conceptId, ownerId, hasGeneratingVariant, reconcileStaleGenerations]);

  return useMemo(
    () => ({
      document: query.data?.document ?? null,
      reconciliation: query.data?.reconciliation ?? null,
      setRowId: query.data?.setRowId ?? null,
      ownerId,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,

      saveSet,
      saveVariant,
      flushAutosave: () => queue.flushAll(),
      hasUnsavedChanges: pendingKeys.length > 0,
      pendingKeys,

      ensureRenditions,
      addRatio,
      removeRatio,
      setGenerating,
      attachMedia,
      reconcileStaleGenerations,
      timedOutRenditionIds,

      conflict,
      reload,
    }),
    [
      query.data,
      query.isLoading,
      query.isError,
      query.error,
      ownerId,
      saveSet,
      saveVariant,
      queue,
      pendingKeys,
      ensureRenditions,
      addRatio,
      removeRatio,
      setGenerating,
      attachMedia,
      reconcileStaleGenerations,
      timedOutRenditionIds,
      conflict,
      reload,
    ],
  );
}
